import React from 'react';
import type { QueryCache } from '@tanstack/react-query';
import { getSalaryCalcsForTable } from '../../api/salaryCalc';
import { queryClient } from '../../lib/queryClient';
import type { CodeValueType } from '../../types/types';
import { classifySalaryCalcResult, SalaryFetchOutcome } from './allCoachesSalary';

/** 표 전용 동시 조회 수 — 강사 수만큼 한꺼번에 부르지 않는다 */
export const ALL_COACHES_SALARY_CONCURRENCY = 3;
/** 강사 한 명 요청의 제한 시간 — 넘으면 그 행만 「불러오지 못함」, 나머지는 계속 */
export const ALL_COACHES_SALARY_TIMEOUT_MS = 30_000;
/** 기존 급여 조회 키 — 요율·회차 보정·분류·부가세 저장 등이 이 키를 무효화한다 */
const SALARY_QUERY_KEY_HEAD = 'getSalaryCalcsQuery';

export type SalaryTableFetcher = (params: {
    year: string;
    month: string;
    selectedCoachId: string;
    centerId: string;
    accessToken: string;
    signal?: AbortSignal;
}) => Promise<any>;

/**
 * 「전체 강사」 급여표 조회 큐.
 * - React Query 관찰자를 강사 수만큼 띄우지 않는다. 표가 직접 순서대로(동시 3) 부른다.
 * - 보기에 들어올 때마다 새로 부른다(같은 달이어도) — 강사별 화면에서 요율·회차 보정 등을 저장했을 수 있다.
 * - 보기에 있는 동안 기존 급여 조회(getSalaryCalcsQuery)가 무효화되면 결과를 비우고 다시 부른다.
 * - 보기를 떠나거나(강사별 전환·탭 이동·언마운트) 달·센터·강사 목록이 바뀌면 남은 큐를 멈추고 진행 중 요청을 끊는다.
 *   늦게 온 응답은 버린다.
 * - 강사 한 명 요청이 제한 시간을 넘으면 그 행만 실패로 두고 큐는 계속 간다(다시 불러오기·엑셀이 잠기지 않게).
 * - invalidate(): 결과를 비우고, 보기에 있으면 바로 다시 부른다(급여 저장 성공 후·다시 불러오기).
 */
export const useAllCoachesSalary = ({
    centerId,
    accessToken,
    year,
    month,
    coaches,
    enabled,
    fetcher = getSalaryCalcsForTable,
    concurrency = ALL_COACHES_SALARY_CONCURRENCY,
    timeoutMs = ALL_COACHES_SALARY_TIMEOUT_MS,
    queryCache = queryClient.getQueryCache(),
}: {
    centerId: string;
    accessToken: string;
    year: string;
    month: string;
    coaches: CodeValueType[];
    enabled: boolean;
    fetcher?: SalaryTableFetcher;
    concurrency?: number;
    timeoutMs?: number;
    queryCache?: QueryCache;
}) => {
    const [version, setVersion] = React.useState(0);
    // 보기에 들어올 때마다 새 표식 — 같은 달이어도 다시 부른다(강사별 화면에서 요율·회차 보정 등을 저장했을 수 있다)
    const entryRef = React.useRef(0);
    const prevEnabledRef = React.useRef(false);
    if (enabled && !prevEnabledRef.current) entryRef.current += 1;
    prevEnabledRef.current = enabled;
    const coachIds = coaches.map((coach) => coach.code).join(',');
    const runKey = `${centerId}|${year}|${month}|${coachIds}#${version}#${entryRef.current}`;

    const [state, setState] = React.useState<{ runKey: string | null; results: Record<string, SalaryFetchOutcome> }>({
        runKey: null,
        results: {},
    });
    const startedKeyRef = React.useRef<string | null>(null);
    const runIdRef = React.useRef(0);
    const controllerRef = React.useRef<AbortController | null>(null);
    const fetcherRef = React.useRef(fetcher);
    fetcherRef.current = fetcher;
    const coachesRef = React.useRef(coaches);
    coachesRef.current = coaches;

    const stopRun = React.useCallback(() => {
        controllerRef.current?.abort();
        controllerRef.current = null;
        runIdRef.current += 1;
        startedKeyRef.current = null;
    }, []);

    const invalidate = React.useCallback(() => setVersion((v) => v + 1), []);

    // 보기를 떠나면(강사별 전환) 남은 큐를 멈추고 진행 중 요청을 끊는다. 다시 들어오면 새 표식으로 처음부터 부른다.
    React.useEffect(() => {
        if (!enabled) stopRun();
    }, [enabled, stopRun]);

    // 보기에 있는 동안 기존 급여 조회가 무효화되면(요율·회차 보정·분류 저장 등) 다시 부른다.
    React.useEffect(() => {
        if (!enabled) return undefined;
        let scheduled = false;
        const unsubscribe = queryCache.subscribe((event: any) => {
            if (event?.type !== 'updated') return;
            if (event?.query?.queryKey?.[0] !== SALARY_QUERY_KEY_HEAD) return;
            const actionType = event?.action?.type;
            // invalidate 이벤트는 쿼리가 이미 무효 상태(직전 재조회가 오류로 끝남)면 다시 나지 않는다(query-core invalidate()).
            // 그래서 「무효 상태에서 시작한 재조회(fetch)」도 무효화로 본다. 포커스 재조회는 전역 refetchOnWindowFocus:false 라 없다.
            const isInvalidation =
                actionType === 'invalidate' || (actionType === 'fetch' && event?.query?.state?.isInvalidated === true);
            if (!isInvalidation) return;
            // 한 번의 무효화가 쿼리 여러 개에 이벤트를 내므로 한 번으로 묶는다
            if (scheduled) return;
            scheduled = true;
            Promise.resolve().then(() => {
                scheduled = false;
                invalidate();
            });
        });
        return unsubscribe;
    }, [enabled, queryCache, invalidate]);

    // 표식이 바뀌면 진행 중 조회를 끊고, 그 조회의 늦은 응답은 runId 가 달라 버려진다.
    React.useEffect(() => {
        if (startedKeyRef.current !== null && startedKeyRef.current !== runKey) {
            stopRun();
        }
    }, [runKey, stopRun]);

    React.useEffect(() => {
        if (!enabled || !centerId || coachesRef.current.length === 0) return;
        if (startedKeyRef.current === runKey) return;
        startedKeyRef.current = runKey;
        const runId = ++runIdRef.current;
        const controller = new AbortController();
        controllerRef.current = controller;
        setState({ runKey, results: {} });

        const queue = [...coachesRef.current];
        const isCurrent = () => runIdRef.current === runId && !controller.signal.aborted;

        const fetchOne = async (coach: CodeValueType): Promise<SalaryFetchOutcome> => {
            const requestController = new AbortController();
            const abortRequest = () => requestController.abort();
            controller.signal.addEventListener('abort', abortRequest);
            let timer: ReturnType<typeof setTimeout> | undefined;
            const timeout = new Promise<'timeout'>((resolve) => {
                timer = setTimeout(() => {
                    requestController.abort();
                    resolve('timeout');
                }, timeoutMs);
            });
            try {
                // 요청이 signal 을 무시하고 끝나지 않아도 제한 시간에 큐가 풀리게 경주시킨다
                const result = await Promise.race([
                    fetcherRef.current({
                        year,
                        month,
                        selectedCoachId: coach.code,
                        centerId,
                        accessToken,
                        signal: requestController.signal,
                    }),
                    timeout,
                ]);
                if (result === 'timeout') return { kind: 'error', reason: 'timeout' };
                return classifySalaryCalcResult(result);
            } catch (error) {
                return classifySalaryCalcResult(undefined, error ?? new Error('unknown'));
            } finally {
                if (timer) clearTimeout(timer);
                controller.signal.removeEventListener('abort', abortRequest);
            }
        };

        const worker = async () => {
            while (queue.length > 0 && isCurrent()) {
                const coach = queue.shift()!;
                const outcome = await fetchOne(coach);
                if (!isCurrent()) return;
                setState((prev) =>
                    prev.runKey !== runKey ? prev : { runKey, results: { ...prev.results, [coach.code]: outcome } },
                );
            }
        };
        for (let i = 0; i < Math.max(1, concurrency); i += 1) {
            void worker();
        }
    }, [enabled, runKey, centerId, accessToken, year, month, concurrency, timeoutMs]);

    React.useEffect(() => () => stopRun(), [stopRun]);

    const results = state.runKey === runKey ? state.results : {};
    const loadedCount = coaches.filter((coach) => !!results[coach.code]).length;
    return {
        results,
        loadedCount,
        total: coaches.length,
        isLoading: enabled && loadedCount < coaches.length,
        invalidate,
    };
};
